import { Card, Divider, Link, Paper } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Form, Formik } from "formik";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import LabelsContext from "../../../../../lang/LabelsContext";
import GeneralInformationService, {
  GeneralInformationServiceType,
} from "../../../../../service/manuscript/general-information/GeneralInformationService";
import EnumYesNo from "../../../../../utils/enum/EnumYesNo";
import HtmlTooltip from "../../../../system/html-tooltip/HtmlTooltip";
import { abstract, articleTitle, correction, coverLetter, firstTime, keywords, previousId, type } from "../TooltipText";
import { useStyles } from "./GeneralInformationStyle";
import TransferList from "./TransferList";
// @ts-ignore
import MUIEditor, { MUIEditorState } from "react-mui-draft-wysiwyg";
import Files from "utils/Files";
import EnumManuscriptType from "utils/enum/EnumManuscriptType";
import { muiControls, preventDefaultEnter } from "../../../../../utils/Utils";

export default function GeneralInformation({ article, setArticle, handleNext }: any) {
  const classes = useStyles();
  const history = useHistory();
  const { Labels } = LabelsContext();
  const { types, firstTimeOptions, articleSave, wordCount }: GeneralInformationServiceType =
    GeneralInformationService();

  const validationSchema = yup.object().shape({
    articleTitle: yup.string().required(Labels.TITLE_REQUIRED),
    type: yup
      .object()
      .shape({
        name: yup.string(),
        code: yup.string(),
      })
      .nullable()
      .required("Manuscript is required."),
  });

  const InfoIcon = (props: any) => (
    <HtmlTooltip title={props.title} interactive>
      <HelpOutlineIcon fontSize="small" className={classes.helpIcon} />
    </HtmlTooltip>
  );

  return (
    <>
      <CssBaseline />
      <div className={classes.layout}>
        <Paper className={classes.paper}>
          <Formik
            initialValues={{
              runningTitle: article.runningTitle ? article.runningTitle : "",
              articleTitle: article.articleTitle ? article.articleTitle : "",
              type: article.type ? article.type : null,
              firstTime: article.firstTime ? article.firstTime : { name: Labels.NO, code: EnumYesNo.NO },
              previousId: article.previousId ? article.previousId : "",
              coverLetter: article.coverLetter ? article.coverLetter : MUIEditorState.createEmpty(),
              keywords: article.keywords ? article.keywords : [],
              abstractText: article.abstractText ? article.abstractText : MUIEditorState.createEmpty(),
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              articleSave(values, article, setArticle, handleNext);
            }}
          >
            {({ handleBlur, values, touched, errors, handleChange, handleSubmit, setFieldValue }) => (
              <Form onKeyDown={preventDefaultEnter}>
                <Grid container spacing={1}>
                  <Grid item className={classes.gridRow} xs={12}>
                    <TextField
                      variant="outlined"
                      margin="normal"
                      id="articleTitle"
                      name="articleTitle"
                      label={Labels.TITLE}
                      value={values.articleTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.articleTitle && Boolean(errors.articleTitle)}
                      helperText={touched.articleTitle && errors.articleTitle}
                      fullWidth
                      required
                    />
                    <InfoIcon title={articleTitle} />
                  </Grid>
                  <Grid item className={classes.gridRow} xs={12}>
                    <TextField
                      variant="outlined"
                      margin="normal"
                      id="runningTitle"
                      name="runningTitle"
                      label={Labels.COVER_LETTER}
                      value={values.runningTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.runningTitle && Boolean(errors.runningTitle)}
                      helperText={touched.runningTitle && errors.runningTitle}
                      fullWidth
                      // required
                      inputProps={{ maxLength: 50 }}
                    />
                    <InfoIcon title={coverLetter} />
                  </Grid>
                  <Grid item className={classes.gridRow} xs={12}>
                    <Link href={Files.TemplateDoc} variant="body2" target="_blank" style={{ color: "red" }}>
                      Download the template
                    </Link>
                  </Grid>

                  <Grid item className={classes.gridRow} xs={12}>
                    <Autocomplete
                      id="combo-box-demo"
                      options={types}
                      getOptionLabel={(option) => option.name}
                      value={values.type}
                      onChange={(event, value) => setFieldValue("type", value)}
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          margin="normal"
                          id="type"
                          name="type"
                          label="Manuscript"
                          onBlur={handleBlur}
                          error={touched.type && Boolean(errors.type)}
                          helperText={touched.type && errors.type}
                          required
                        />
                      )}
                    />
                    <InfoIcon
                      title={
                        <>
                          {type(types)}
                          <Link href={Files.TemplateDoc} variant="body2" target="_blank" style={{ color: "red" }}>
                            Download the template
                          </Link>
                        </>
                      }
                    />
                  </Grid>
                  <Grid item className={classes.gridRow} xs={12}>
                    <Autocomplete
                      id="combo-box-demo"
                      options={firstTimeOptions}
                      getOptionLabel={(option) => option.name}
                      value={values.firstTime}
                      onChange={(event, value) => setFieldValue("firstTime", value)}
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          margin="normal"
                          id="firstTime"
                          name="firstTime"
                          label={Labels.FIRST_TIME}
                        />
                      )}
                    />
                    <InfoIcon title={firstTime} />
                  </Grid>
                  {values.firstTime?.code === EnumYesNo.YES && (
                    <Grid item className={classes.gridRow} xs={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="previousId"
                        name="previousId"
                        label={Labels.PREVIOUS_ID}
                        value={values.previousId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.previousId && Boolean(errors.previousId)}
                        helperText={touched.previousId && errors.previousId}
                        fullWidth
                        // required
                      />
                      <InfoIcon title={previousId} />
                    </Grid>
                  )}
                  <Grid item className={classes.gridRow} xs={12}>
                    <Grid className={classes.widthFull}>
                      <Card className={classes.card}>
                        <Typography variant="subtitle1">Cover Letter / Comments to the Editor</Typography>
                        <Divider className={classes.divider} />
                        <MUIEditor
                          config={{
                            toolbar: { controls: muiControls },
                            draftEditor: {
                              placeholder: Labels.PLACEHOLDER_WRITE_COMMENT,
                              stripPastedStyles: true,
                            },
                          }}
                          editorState={values.coverLetter}
                          onChange={(state: any) => {
                            setFieldValue("coverLetter", state);
                          }}
                        />
                      </Card>
                    </Grid>
                    <InfoIcon title={correction} />
                  </Grid>
                  <Grid item className={classes.gridRow} xs={12}>
                    <TransferList fieldName="keywords" values={values.keywords} setFieldValue={setFieldValue} />
                    <InfoIcon title={keywords} />
                  </Grid>
                  {values?.type?.id !== EnumManuscriptType.LETTER_TO_EDITOR && (
                    <Grid item className={classes.gridRow} xs={12}>
                      <Grid className={classes.widthFull}>
                        <Card className={classes.card}>
                          <Typography variant="subtitle1">{Labels.ABSTRACT}</Typography>
                          <Divider className={classes.divider} />
                          <MUIEditor
                            config={{
                              toolbar: { controls: muiControls },
                              draftEditor: {
                                placeholder: "Write an abstract..",
                                stripPastedStyles: true,
                              },
                            }}
                            editorState={values.abstractText}
                            onChange={(state: any) => {
                              setFieldValue("abstractText", state);
                            }}
                          />
                          {[EnumManuscriptType.REVIEW, EnumManuscriptType.ORIGINAL_CONTRIBUTION].includes(
                            values?.type?.id
                          ) && (
                            <Typography style={{ textAlign: "end" }}>{`${wordCount(values?.abstractText)} / ${
                              values?.type?.id === EnumManuscriptType.REVIEW ? 200 : 250
                            }`}</Typography>
                          )}
                        </Card>
                      </Grid>
                      <InfoIcon title={abstract} />
                    </Grid>
                  )}
                  <Grid item className={classes.gridRow} xs={12}>
                    <Typography variant="body2">{Labels.COPY_ITEMS}</Typography>
                  </Grid>
                </Grid>
                <div className={classes.buttons}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={(e) => history.push("/")}
                    className={classes.button}
                  >
                    {Labels.CANCEL}
                  </Button>
                  <Button variant="contained" color="primary" className={classes.button} type="submit">
                    {Labels.NEXT}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Paper>
      </div>
    </>
  );
}
